import { useEffect, useMemo, useState } from 'react';
import { endOfMonth, startOfMonth } from 'date-fns';

import {
    Box, Divider, FormControl, Grid, InputLabel,
    MenuItem, Select, Stack, Typography
} from '@mui/material';

import { months } from '../../../../constants/constants';
import { buildDailyNewCustomerMetrics } from '../../../../helpers/admin/dashboard/dashboardHelper';
import DashboardCard from '../components/DashboardCard';
import SemesterChart from './SemesterChart';
import DashboardOrdersExportButton from '../components/DashboardOrdersExportButton';

const DailyNewCustomers = ({ customers, today }) => {
    const year = today.getFullYear();
    const [month, setMonth] = useState(today.getMonth());
    const [day, setDay] = useState(today.getDate());
    const metrics = useMemo(
        () => buildDailyNewCustomerMetrics(customers, year, month),
        [customers, month, year]
    );
    const selectedDay = metrics.rows[day - 1] ?? metrics.rows[0];
    const selectedMonthDate = new Date(year, month, 1);

    useEffect(() => {
        setDay(currentDay => Math.min(currentDay, metrics.rows.length));
    }, [metrics.rows.length]);

    useEffect(() => {
        setMonth(today.getMonth());
        setDay(today.getDate());
    }, [today]);

    const chartData = {
        labels: metrics.labels,
        datasets: [{
            label: 'Clientes nuevos',
            data: metrics.values,
            backgroundColor: 'rgba(54, 162, 235, 0.55)'
        }]
    };

    return (
        <DashboardCard
            className="clientes-nuevos-card"
            title={`Clientes nuevos por día (${year})`}
            action={
                <DashboardOrdersExportButton
                    reportType="new_customers"
                    range={{
                        startDate: startOfMonth(selectedMonthDate),
                        endDate: endOfMonth(selectedMonthDate)
                    }}
                    filename={`pedidos_clientes-nuevos_${year}-${String(month + 1).padStart(2, '0')}.csv`}
                />
            }
        >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <FormControl size="small" fullWidth>
                    <InputLabel id="new-customers-month-label">Mes</InputLabel>
                    <Select
                        labelId="new-customers-month-label"
                        value={month}
                        label="Mes"
                        onChange={({ target }) => setMonth(Number(target.value))}
                    >
                        {months.map((monthName, index) => (
                            <MenuItem key={monthName} value={index}>{monthName}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                    <InputLabel id="new-customers-day-label">Día</InputLabel>
                    <Select
                        labelId="new-customers-day-label"
                        value={day}
                        label="Día"
                        onChange={({ target }) => setDay(Number(target.value))}
                    >
                        {metrics.rows.map(row => (
                            <MenuItem key={row.day} value={row.day}>{row.day}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <SemesterChart label={months[month]} data={chartData} />
            <Divider sx={{ my: 2.5 }} />
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                            {selectedDay?.count ?? 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {selectedDay?.date ?? ''}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                            {metrics.total}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total del mes
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </DashboardCard>
    );
};

export default DailyNewCustomers;
